import _get from 'lodash/get';
import _set from 'lodash/set';
import _isEmpty from 'lodash/isEmpty';
import _isInteger from 'lodash/isInteger';
import {
  COLUMN_NO,
  COLUMN_MULTI_STOP_SEQUENCE_NUMBER,
  getUploadSpreadsheetDataArea,
  PARSE_EXCEL_ERROR_CODE,
} from 'constants/new-bulk-upload-const';
import _cloneDeep from 'lodash/cloneDeep';
import uuidV4 from 'uuid/v4';
import _every from 'lodash/every';
import {
  parseColumnIndexes,
  getUploadWorksheetFromWorkbook,
} from '../common/new-bulk-upload-common';

const rowHasData = (currentRow, parsedColumns) => {
  const colNoIndex = parsedColumns[COLUMN_NO].index;
  const columnKeys = Object.keys(parsedColumns);
  for (let i = 0; i < columnKeys.length; i++) {
    const col = parsedColumns[columnKeys[i]];
    if (
      col.index !== colNoIndex &&
      col.required &&
      currentRow.getCell(col.index).value
    ) {
      // Has at least one required field
      return true;
    }
  }
  return false;
};

// Validate if uploaded format has correct columns number, and don't miss any required column
const validateWorksheetFormat = (workSheet, featureFlags) => {
  // Check if MSD columns are present when enableMSD is false
  if (!_get(featureFlags, 'enableMSD', false)) {
    // Check if there's any data in columns T (20) and U (21) when enableMSD is false
    const dataStartRow = 4; // Data starts after header rows
    const dataEndRow = workSheet.actualRowCount;

    for (let rowIndex = dataStartRow; rowIndex <= dataEndRow; rowIndex++) {
      const row = workSheet.getRow(rowIndex);
      const columnT = row.getCell(20); // Column T
      const columnU = row.getCell(21); // Column U

      if (
        (columnT.value !== null &&
          columnT.value !== undefined &&
          columnT.value !== '') ||
        (columnU.value !== null &&
          columnU.value !== undefined &&
          columnU.value !== '')
      ) {
        throw { errorCode: PARSE_EXCEL_ERROR_CODE.TEMPLATE_ERROR };
      }
    }
  }

  // Parse and check the column
  const parsedColumns = parseColumnIndexes(workSheet, featureFlags);
  Object.keys(parsedColumns).forEach(key => {
    const col = parsedColumns[key];
    if (col.required && col.index === -1) {
      // raise an issue
      throw { errorCode: PARSE_EXCEL_ERROR_CODE.TEMPLATE_ERROR };
    }
  });

  return parsedColumns;
};

// Parse row & validate if there is any missing data in required column
const validateRequiredRowDataWithMetaData = (
  currentRow,
  parsedColumns,
  rowIndex,
  metaData,
) => {
  // parse it
  const parsedRow = _cloneDeep(parsedColumns);
  Object.keys(parsedRow).forEach(key => {
    const colData = parsedRow[key];
    const dataByIndex = _get(currentRow.values, [colData.index]);

    if (
      colData.required &&
      (dataByIndex === null || dataByIndex === undefined)
    ) {
      throw {
        errorCode: PARSE_EXCEL_ERROR_CODE.REQUIRED_DATA_ERROR,
        fieldName: key,
        rowIndex,
      };
    }
    colData.data =
      colData.validator && typeof colData.validator === 'function'
        ? colData.validator(dataByIndex, rowIndex, metaData)
        : dataByIndex;
  });

  return parsedRow;
};

const mapRowToReduxName = (parsedRow, rowIndex) => {
  // convert it to redux object
  const validDataRow = {};
  Object.keys(parsedRow).forEach(key => {
    validDataRow[parsedRow[key].name] = parsedRow[key].data;
    validDataRow.rowIndex = rowIndex;
  });
  validDataRow.id = uuidV4();
  return validDataRow;
};

const sortAndValidateBatchRowHasSameSender = batchRows => {
  // / It's already in number format
  if (_isEmpty(batchRows)) {
    return batchRows;
  }

  const sequenceFieldName = 'sequenceNumber';
  const isNotPresetSequence = 'isNotPresetSequence';
  const sortedRows = batchRows.sort(
    (a, b) => _get(a, sequenceFieldName) - _get(b, sequenceFieldName),
  );

  const hasSequenceNumber = _every(sortedRows, row =>
    _get(row, sequenceFieldName),
  );

  if (!hasSequenceNumber) {
    // This mean user not preset sequence number or partially
    let currentSequenceNumber = 1;
    sortedRows.forEach(row => {
      _set(row, sequenceFieldName, currentSequenceNumber++);
      _set(row, isNotPresetSequence, true);
    });
  }

  const firstRowInBatch = sortedRows[0];
  for (let i = 1; i < sortedRows.length; i++) {
    const currentRow = sortedRows[i];
    if (
      _get(firstRowInBatch, 'senderName') !== _get(currentRow, 'senderName') ||
      _get(firstRowInBatch, 'senderPhone') !==
        _get(currentRow, 'senderPhone') ||
      _get(firstRowInBatch, 'pickUp') !== _get(currentRow, 'pickUp') ||
      _get(firstRowInBatch, 'senderAddressDetail') !==
        _get(currentRow, 'senderAddressDetail')
    ) {
      throw {
        errorCode: PARSE_EXCEL_ERROR_CODE.MULTISTOP_VALIDATION_ERROR,
        fieldName: COLUMN_MULTI_STOP_SEQUENCE_NUMBER,
        rowIndex: _get(currentRow, 'rowIndex'),
      };
    }
  }

  return sortedRows;
};

const sortBatchOrdering = parsedRowList => {
  const batchFieldName = 'batch';
  const hasBatchNumberRows = parsedRowList.filter(currentRow =>
    _isInteger(_get(currentRow, batchFieldName)),
  );
  if (_isEmpty(hasBatchNumberRows)) {
    return parsedRowList;
  }
  const hasNoBatchNumberRows = parsedRowList.filter(
    currentRow => !_isInteger(_get(currentRow, batchFieldName)),
  );

  // / Group all rows
  let groupedBatchNumberRows = [];
  hasBatchNumberRows.forEach(currentRow => {
    const currentBatchNumber = _get(currentRow, batchFieldName);
    let groupedRow = groupedBatchNumberRows.filter(
      p => p.rawBatch === currentBatchNumber,
    )[0];
    if (!groupedRow) {
      groupedRow = {
        rawBatch: currentBatchNumber,
        rows: [],
      };
      groupedBatchNumberRows.push(groupedRow);
    }
    groupedRow.rows.push(currentRow);
  });

  let currentBatchNumber = 1;
  const sortedRows = [];
  groupedBatchNumberRows = groupedBatchNumberRows.sort(
    (a, b) => a.rawBatch - b.rawBatch,
  );
  groupedBatchNumberRows.forEach(groupRow => {
    groupRow.rows.forEach(row => {
      _set(row, batchFieldName, currentBatchNumber);
    });
    currentBatchNumber++;
    const rows = sortAndValidateBatchRowHasSameSender(groupRow.rows);
    sortedRows.push(...rows);
  });

  sortedRows.push(...hasNoBatchNumberRows);

  return sortedRows;
};

export const parseUploadExcel = ({
  workbook,
  metaDataWithVersion,
  bookingConfiguration,
  featureFlags,
}) => {
  // Will throw errorCode and stop process if found an issue during parsing
  const metaData = _get(metaDataWithVersion, 'meta', null);
  const bookingLimit = _get(metaData, 'bookingLimit');
  _set(
    metaData,
    'parcelSizes',
    _get(bookingConfiguration, ['parcelSizes'], []),
  );
  const workSheet = getUploadWorksheetFromWorkbook(workbook);
  if (!workSheet) return;
  const { dataRow } = getUploadSpreadsheetDataArea();

  // Validate the format
  const parsedColumns = validateWorksheetFormat(workSheet, featureFlags);

  // No issue
  let parsedRowList = [];
  const rowsToParse = [];

  // Parse the data
  const dataRows = workSheet.getRows(dataRow, workSheet.rowCount);

  for (let rowIndex = 0; rowIndex < dataRows.length; rowIndex++) {
    const currentRow = dataRows[rowIndex];
    // check if currentRow have data
    if (rowHasData(currentRow, parsedColumns)) {
      rowsToParse.push(currentRow);
    }
  }

  if (bookingLimit && bookingLimit > 0 && rowsToParse.length > bookingLimit) {
    throw {
      errorCode: PARSE_EXCEL_ERROR_CODE.EXCEED_BOOKING_LIMIT_ERROR,
      bookingLimit,
    };
  }

  rowsToParse.forEach((currentRow, rowIndex) => {
    const parsedRow = validateRequiredRowDataWithMetaData(
      currentRow,
      parsedColumns,
      rowIndex,
      metaData,
    );
    const validRow = mapRowToReduxName(parsedRow, rowIndex);
    parsedRowList.push(validRow);
  });
  if (_isEmpty(parsedRowList)) {
    throw {
      errorCode: PARSE_EXCEL_ERROR_CODE.EMPTY_DATA_ERROR,
    };
  }
  if (_get(metaData, 'supportMSD', false)) {
    parsedRowList = sortBatchOrdering(parsedRowList);
  }

  return parsedRowList;
};

export default { parseUploadExcel };
